import { Link } from 'react-router-dom';
import { useT } from '@kmgestao/i18n';
import { Badge, Button, PageSectionCard, Skeleton } from '@kmgestao/ui';

import { useOwnSubscription } from '../../../features/clinic-billing/index.js';

export interface PlanStatusSummaryCardProps {
  clinicId: string;
}

/**
 * Card resumido de "status do plano" no dashboard inicial. Mais
 * compacto que o `CurrentPlanCard` da página `/plano` — só nome do
 * plano + status + CTA "Ver detalhes" linkando pra `/plano`.
 */
export function PlanStatusSummaryCard({ clinicId }: PlanStatusSummaryCardProps) {
  const { t } = useT('clinics');
  const subQ = useOwnSubscription(clinicId);

  return (
    <PageSectionCard
      title={t('dashboard.planSummary.title')}
      description={t('dashboard.planSummary.description')}
      contentClassName="space-y-4"
    >
      {subQ.isLoading ? (
        <PlanSummarySkeleton />
      ) : subQ.isError ? (
        <p className="text-sm text-destructive" role="alert">
          {subQ.error instanceof Error
            ? subQ.error.message
            : t('dashboard.planSummary.unknownError')}
        </p>
      ) : subQ.data ? (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-base font-semibold text-foreground">
              {subQ.data.plan?.name_pt_br ?? '—'}
            </p>
            <Badge variant={statusBadgeVariant(subQ.data.subscription.status)}>
              {t(`dashboard.planSummary.statusLabels.${subQ.data.subscription.status}`, {
                defaultValue: subQ.data.subscription.status,
              })}
            </Badge>
          </div>
          <Button asChild size="sm" variant="outline">
            <Link to="/plano">{t('dashboard.planSummary.viewDetails')}</Link>
          </Button>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">{t('dashboard.planSummary.empty')}</p>
      )}
    </PageSectionCard>
  );
}

function PlanSummarySkeleton() {
  return (
    <div className="space-y-3">
      <Skeleton className="h-5 w-1/2" />
      <Skeleton className="h-9 w-32" />
    </div>
  );
}

function statusBadgeVariant(
  status: string,
): 'default' | 'destructive' | 'success' | 'secondary' | 'outline' {
  switch (status) {
    case 'active':
      return 'success';
    case 'trialing':
      return 'default';
    case 'past_due':
    case 'unpaid':
      // Badge não tem variant 'warning' — usamos 'secondary' (cor neutra-muted)
      return 'secondary';
    default:
      return 'outline';
  }
}
